"use client"

import styled from "@emotion/styled"
import { Box, Chip, MenuItem, Select } from "@mui/material"
import { useTranslations } from "next-intl"
import React, { useCallback, useMemo, useState } from "react"
import { colors, mediaQueries, sizes } from "ui"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"
import ReportCard from "../ReportsCards/ReportCard"
import { ReportFilteredListProps } from "./types"

const StyledButtonWithArrow = styled(ButtonWithArrow)``

const StyledContainer = styled(Box)`
  width: 100%;
  margin: 0 auto;
`

const FilterContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${sizes.baseSingle};
  margin-bottom: ${sizes.baseTriple};
  justify-content: center;
`
const FilterBubblesContainer = styled(FilterContainer)`
  width: 85%;
  justify-content: flex-start;
`

const FilterChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "chipColor",
})<{ isActive: boolean; chipColor?: string }>`
  border-radius: ${sizes.borderRadiusLarge};
  background-color: ${({ isActive, chipColor }) =>
    isActive
      ? chipColor
        ? `${chipColor}20`
        : colors.lightGray
      : colors.white};
  border: 1px solid
    ${({ isActive, chipColor }) =>
      isActive ? (chipColor ? `${chipColor}` : colors.red) : colors.red};
  color: ${colors.black};
  height: unset;
  font-size: 16px;
  font-weight: 400;
  padding: 12px ${sizes.baseDouble};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    border: 1px solid
      ${({ chipColor }) => (chipColor ? `${chipColor}` : colors.gray)};
    background-color: ${({ chipColor }) =>
      chipColor ? `${chipColor}50` : colors.lightGray};
  }
`

const FiltersContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseSingle};
  margin-bottom: ${sizes.baseDouble};

  ${mediaQueries.tablet} {
    flex-direction: row;
    justify-content: center;
  }

  ${mediaQueries.desktopM} {
    position: absolute;
    right: 0;
    top: 0;
    flex-direction: row;
  }
`

const menuProps = {
  PaperProps: {
    sx: {
      borderRadius: sizes.borderRadiusLarge,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
      marginTop: sizes.baseSingle,
      border: `1px solid ${colors.red}`,
      padding: "0",
      [`${mediaQueries.desktopM}`]: {
        padding: "0",
      },
      ".MuiList-root": {
        display: "flex",
        flexDirection: "column",
      },
      "& .MuiMenuItem-root": {
        color: colors.black,
        padding: "12px 20px",
        fontSize: "16px",
        "&:hover": {
          backgroundColor: `${colors.lightGray}`,
        },
      },
    },
  },
}

const StyledSelect = styled(Select)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.red};
  border-radius: ${sizes.borderRadiusLarge};
  padding: 13px 18px;
`

const CardsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.baseDouble};
  position: relative; /* Add position relative for layout stability */
  min-height: 200px; /* Set min-height to prevent container collapse */

  ${mediaQueries.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueries.desktopM} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StyledReportCard = styled(ReportCard)`
  width: 100%;
  /* Remove 'will-change: auto' as it may cause rendering issues */
  /* Replace 'contain: layout style' with safer containment strategy */
  contain: content; /* Use content containment for better performance */
  display: flex;
  flex-direction: column;

  ${mediaQueries.tablet} {
    width: 100%;
  }

  ${mediaQueries.desktopM} {
    width: 100%;
  }
`

const FiltersWrapper = styled(Box)`
  position: relative;
  width: 100%;
  margin-bottom: ${sizes.baseDouble}; /* Add margin to ensure separation from card grid */
`

// Create a stable ReportCard component with proper memoization
const StableReportCard = React.memo<{ data: any; stableKey: string }>(
  ({ data, stableKey }) => <StyledReportCard data={data} />,
  // Custom comparison function for better memoization
  (prevProps, nextProps) => {
    return (
      prevProps.stableKey === nextProps.stableKey &&
      prevProps.data === nextProps.data
    )
  },
)

StableReportCard.displayName = "StableReportCard"

const ReportFilteredList: React.FC<ReportFilteredListProps> = ({
  itemsList,
  reportsFilters,
  button,
}) => {
  const t = useTranslations("ReportsCards")

  // Memoize expensive calculations
  const { filterOptions, yearOptions, reportTypeOptions, filterColors } =
    useMemo(() => {
      if (!itemsList?.length) {
        return {
          filterOptions: [],
          yearOptions: [],
          reportTypeOptions: [],
          filterColors: {},
        }
      }

      // Extract unique source types for filtering
      const types = new Set<string>()
      const years = new Set<string>()
      const reportTypes = new Set<string>()

      itemsList.forEach((item) => {
        if (item.parentName) types.add(item.parentName)

        if (item.publicationDate?.dateTime) {
          const year = new Date(item.publicationDate.dateTime)
            .getFullYear()
            .toString()
          years.add(year)
        }

        if (item.reportType?.value) {
          reportTypes.add(item.reportType.value)
        }
      })

      // Create color mapping
      const colorMap: Record<string, string> = {}
      reportsFilters.forEach((item) => {
        if (item.category && item.primaryColor) {
          colorMap[item.category] = item.primaryColor
        }
      })

      return {
        filterOptions: Array.from(types).filter(Boolean),
        yearOptions: Array.from(years).sort((a, b) => b.localeCompare(a)),
        reportTypeOptions: Array.from(reportTypes).filter(Boolean),
        filterColors: colorMap,
      }
    }, [itemsList, reportsFilters])

  // Initialize with first filter option
  const [activeFilter, setActiveFilter] = useState<string | null>(() =>
    filterOptions.length > 0 ? filterOptions[0] : null,
  )
  const [selectedYear, setSelectedYear] = useState<string>("")
  const [selectedReportType, setSelectedReportType] = useState<string>("")

  // Memoize filtered items to prevent recalculation on every render
  const filteredItems = useMemo(() => {
    if (!itemsList?.length) return []

    return itemsList.filter((item) => {
      // Filter by source type
      if (activeFilter && item.parentName !== activeFilter) {
        return false
      }

      // Filter by year
      if (selectedYear && item.publicationDate?.dateTime) {
        const itemYear = new Date(item.publicationDate.dateTime)
          .getFullYear()
          .toString()
        if (itemYear !== selectedYear) return false
      }

      // Filter by report type
      if (selectedReportType && item.reportType?.value !== selectedReportType) {
        return false
      }

      return true
    })
  }, [itemsList, activeFilter, selectedYear, selectedReportType])

  // Create stable keys for each item to prevent unnecessary re-renders
  const itemsWithStableKeys = useMemo(() => {
    return filteredItems.map((item, index) => ({
      data: item,
      stableKey: `${item.name || "unnamed"}-${item.publicationDate?.dateTime || index}-${index}`,
    }))
  }, [filteredItems])

  // Memoized event handlers to prevent unnecessary re-renders
  const handleFilterClick = useCallback((filter: string) => {
    setActiveFilter(filter)
  }, [])

  const handleYearChange = useCallback((event: any) => {
    setSelectedYear(event.target.value as string)
  }, [])

  const handleReportTypeChange = useCallback((event: any) => {
    setSelectedReportType(event.target.value as string)
  }, [])

  // Early return if no items
  if (!itemsList?.length) return null

  return (
    <StyledContainer>
      <FiltersWrapper>
        {filterOptions.length > 0 && (
          <FilterBubblesContainer>
            {filterOptions.map((filter) => (
              <FilterChip
                key={filter}
                label={filter}
                isActive={activeFilter === filter}
                chipColor={filterColors[filter]}
                onClick={() => handleFilterClick(filter)}
              />
            ))}
          </FilterBubblesContainer>
        )}

        <FiltersContainer>
          {yearOptions.length > 0 && (
            <StyledSelect
              value={selectedYear}
              onChange={handleYearChange}
              displayEmpty
              MenuProps={menuProps}
              renderValue={
                selectedYear !== "" ? undefined : () => t("selectYear")
              }
            >
              <MenuItem value="">{t("allYears")}</MenuItem>
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          )}

          {activeFilter === t("reports") && reportTypeOptions.length > 0 && (
            <StyledSelect
              value={selectedReportType}
              onChange={handleReportTypeChange}
              displayEmpty
              MenuProps={menuProps}
              renderValue={
                selectedReportType !== ""
                  ? undefined
                  : () => t("selectReportType")
              }
            >
              <MenuItem value="">{t("allReportTypes")}</MenuItem>
              {reportTypeOptions.map((type, index) => (
                <MenuItem key={`${type}-${index}`} value={type}>
                  {type}
                </MenuItem>
              ))}
            </StyledSelect>
          )}
        </FiltersContainer>
      </FiltersWrapper>

      {itemsWithStableKeys.length > 0 && (
        <CardsGrid>
          {itemsWithStableKeys.map(({ data, stableKey }) => (
            <StableReportCard
              key={stableKey}
              data={data}
              stableKey={stableKey}
            />
          ))}
        </CardsGrid>
      )}

      {button && button?.links?.length > 0 && (
        <StyledButtonWithArrow href={button.links[0].url}>
          {button.links[0]?.name}
        </StyledButtonWithArrow>
      )}
    </StyledContainer>
  )
}

export default React.memo(ReportFilteredList)
